import APP_ASSETS from "@/assets";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMerchantWrite } from "@/hooks";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import StakeDetails from "./StakeDetails";
import { Skeleton } from "@/components/ui/skeleton";
import { useUSDT, useMerchant } from "@/contexts";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button/button";
import { LocalStorageKeys } from "@/constants";
import { trackAmplitudeEvent } from "@/utils/amplitude";

const Stake = () => {
  const [stakeAmount, setStakeAmount] = useState("");
  const [isStaking, setIsStaking] = useState(false);
  const [upiId, setUpiId] = useState("");
  const { unstakedRequested } = useMerchant();

  const { toast } = useToast();

  const {
    usdtBalance,
    isLoading: balanceLoading,
    hasFetched: balanceFetched,
    refreshUSDT,
  } = useUSDT();
  const {
    merchantDetails,
    merchantConfig,
    isLoading: merchantLoading,
    hasFetched: merchantFetched,
    refreshMerchant,
  } = useMerchant();

  const isRegistering = useMemo(() => {
    return !merchantDetails?.isRegistered;
  }, [merchantDetails]);

  useEffect(() => {
    if (
      isRegistering &&
      !merchantLoading &&
      merchantFetched &&
      merchantConfig
    ) {
      setStakeAmount(String(merchantConfig.minStake));
    }
  }, [isRegistering]);

  const { registerByStaking, stake, requestUnstake } = useMerchantWrite();

  const handleRegisterOrStake = async () => {
    setIsStaking(true);
    if (isRegistering) {
      trackAmplitudeEvent("MERCHANT_REGISTRATION_INITIATED", { stakeAmount });
      const result = await registerByStaking(stakeAmount);
      if (result?.afterWait?.status === "success") {
        trackAmplitudeEvent("MERCHANT_REGISTRATION_SUCCESS", { stakeAmount });
        localStorage.setItem(LocalStorageKeys.MERCHANT_UPI, upiId);
        toast({
          title: "Registration Successful",
          description: `Registered successfully by staking ${stakeAmount} USDC.`,
        });
      } else {
        trackAmplitudeEvent("MERCHANT_REGISTRATION_FAILED", {
          stakeAmount,
          error: result?.message,
        });
        toast({
          title: "Registration Failed",
          description: `Request for registration by staking ${stakeAmount} USDC wasn't successful.`,
          variant: "destructive",
        });
      }
    } else {
      trackAmplitudeEvent("STAKE_INITIATED", { stakeAmount });
      const result = await stake(stakeAmount);
      //@ts-ignore
      if (result?.afterWait?.status === "success") {
        trackAmplitudeEvent("STAKE_SUCCESS", { stakeAmount });
        toast({
          title: "Staking Successful",
          description: `Staked USDC successfully.`,
        });
      } else {
        trackAmplitudeEvent("STAKE_FAILED", {
          stakeAmount,
          error: result?.message,
        });
        toast({
          title: "Staking Failed",
          description: `Request for staking USDC wasn't successful.`,
          variant: "destructive",
        });
      }
    }
    setIsStaking(false);
    void refreshUSDT();
    void refreshMerchant();
  };

  const handleUnstakeRequest = async () => {
    setIsStaking(true);
    trackAmplitudeEvent("UNSTAKE_INITIATED");

    const result = await requestUnstake();
    //@ts-ignore
    if (result?.afterWait?.status === "success") {
      trackAmplitudeEvent("UNSTAKE_SUCCESS");
      toast({
        title: "Unstake Request Successful",
        description: `Request for unstaking USDC submitted.`,
      });
      await refreshMerchant();
    } else {
      trackAmplitudeEvent("UNSTAKE_FAILED", { error: result?.message });
      toast({
        title: "Unstake Request Failed",
        description: result.message,
        variant: "destructive",
      });
    }
    setIsStaking(false);
  };

  return (
    <main className="relative h-full max-h-[1200px] w-full max-w-[500px] bg-bg-tertiary">
      {/* ABSOLUTE POSITIONED TOPBAR */}
      <nav className="absolute top-0 flex h-14 w-full items-center justify-between border-b-4 border-primary bg-bg-tertiary p-4">
        <div className="title-container flex items-center gap-2">
          <Link to={isRegistering ? "/onboarding" : ".."} relative="path">
            <img
              className="h-6 w-6"
              src={APP_ASSETS.ICONS.ARROW_LEFT}
              alt="img"
            />
          </Link>
          <p className="text-xl text-text-light">Stake</p>
        </div>
        <div className="close-btn-container">
          <Link
            to={isRegistering ? "/onboarding" : ".."}
            relative="path"
            className="flex w-full">
            <img
              className="flex h-6 w-full self-end"
              src={APP_ASSETS.ICONS.Close_Btn}
              alt="img"
            />
          </Link>
        </div>
      </nav>
      {/* ABSOLUTE POSITIONED TOPBAR */}
      {/* BODY */}
      <section className="no-scrollbar flex h-full flex-col items-start gap-4 overflow-y-scroll px-6 pt-16">
        {/* HEADER */}
        <div className="flex flex-col items-start justify-center gap-2">
          <p className="text-2xl font-bold text-text-primary">Stake USDC</p>
          <p className="text-xs text-text-light">
            Deposit USDC to your P2P.me Base Network address and stake it on our
            protocol. Higher staked USDC boosts your order volume and sizes.
          </p>
        </div>
        {/* HEADER */}
        {/* DETAILS */}
        <StakeDetails key={Number(isStaking)} />
        {/* DETAILS */}
        {/* TABS */}
        <div className="flex w-full flex-col justify-center gap-2">
          <Tabs defaultValue="stake">
            {!isRegistering ? (
              <TabsList className="my-2 border-[1px] border-stroke-grey bg-none">
                <TabsTrigger value="stake" className="rounded-l-md font-bold">
                  Stake
                </TabsTrigger>
                <TabsTrigger className="rounded-r-md font-bold" value="unstake">
                  Unstake
                </TabsTrigger>
              </TabsList>
            ) : null}
            <TabsContent value="stake">
              <div className="flex flex-col justify-center gap-1">
                <label
                  className="text-[14px] font-bold tracking-wide text-text-primary"
                  htmlFor="stakeAmount">
                  Amount to stake
                </label>
                <input
                  className={cn(
                    "rounded-md border border-gray-800 bg-white p-2 text-sm text-black",
                  )}
                  type="number"
                  min={0}
                  name="stakeAmount"
                  value={stakeAmount}
                  onChange={(e) => {
                    setStakeAmount(e.target.value);
                  }}
                  placeholder="Enter amount"
                />
                {isRegistering ? (
                  <>
                    {" "}
                    <label
                      className="text-[14px] font-bold tracking-wide text-text-primary"
                      htmlFor="stakeAmount">
                      Enter your UPI ID
                    </label>
                    <input
                      className={cn(
                        "rounded-md border border-gray-800 bg-white p-2 text-sm text-black",
                      )}
                      type="string"
                      min={0}
                      name="upiId"
                      value={upiId}
                      onChange={(e) => {
                        setUpiId(e.target.value);
                      }}
                      placeholder="Enter UPI ID"
                    />
                    {upiId === "" ? (
                      <div className="flex items-start gap-1 py-1">
                        <img
                          className="mt-0.5 h-3 w-3"
                          src={APP_ASSETS.ICONS.ERROR_INFO}
                          alt="errorInfo"
                        />
                        <p className="text-xs text-red-500">
                          Please enter you UPI ID.
                        </p>
                      </div>
                    ) : null}
                  </>
                ) : null}
                {!isRegistering ? (
                  <>
                    <div className="flex items-center justify-between">
                      <label
                        className="flex text-xs tracking-wide text-text-light"
                        htmlFor="asset">
                        Balance:{" "}
                        {!balanceLoading && balanceFetched ? (
                          (usdtBalance ?? 0)
                        ) : (
                          <Skeleton className="h-4 w-6" />
                        )}{" "}
                        USDC
                      </label>
                      <div className="flex gap-1">
                        <button
                          disabled={balanceLoading && !balanceFetched}
                          onClick={() => {
                            trackAmplitudeEvent(
                              "STAKE_AMOUNT_50_PERCENT_CLICKED",
                            );
                            setStakeAmount(String((usdtBalance ?? 0) / 2));
                          }}
                          className="flex items-center justify-center rounded-full border border-primary">
                          <p className="px-4 text-xs text-text-primary">50%</p>
                        </button>
                        <button
                          disabled={balanceLoading && !balanceFetched}
                          onClick={() => {
                            trackAmplitudeEvent("STAKE_AMOUNT_MAX_CLICKED");
                            setStakeAmount(String(usdtBalance ?? 0));
                          }}
                          className="flex items-center justify-center rounded-full border border-primary">
                          <p className="px-4 text-xs text-text-primary">Max</p>
                        </button>
                      </div>
                    </div>
                    <div className="flex items-start gap-1 py-1">
                      <img
                        className="mt-0.5 h-3 w-3"
                        src={APP_ASSETS.ICONS.ERROR_INFO}
                        alt="errorInfo"
                      />
                      <p className="text-xs text-red-500">
                        Increase your USDC stake for access to larger order
                        sizes.
                      </p>
                    </div>
                  </>
                ) : null}
              </div>
              {isRegistering ? (
                <div className="mt-2">
                  <Button
                    disabled={
                      stakeAmount === "" ||
                      stakeAmount === "0" ||
                      isStaking ||
                      Number(stakeAmount) > usdtBalance ||
                      (isRegistering &&
                        Number(stakeAmount) <
                          (merchantConfig?.minStake as number)) ||
                      upiId === ""
                    }
                    loading={isStaking}
                    label={`Stake ${
                      isRegistering ? `${stakeAmount} USDC and Register` : ""
                    }`}
                    onClick={handleRegisterOrStake}
                    widthFull
                  />
                </div>
              ) : (
                <div className="mt-2">
                  <Button
                    disabled={
                      stakeAmount === "" ||
                      stakeAmount === "0" ||
                      isStaking ||
                      Number(stakeAmount) > usdtBalance
                    }
                    loading={isStaking}
                    label={`Stake ${
                      isRegistering ? `${stakeAmount} USDC and Register` : ""
                    }`}
                    onClick={handleRegisterOrStake}
                    widthFull
                  />
                </div>
              )}
            </TabsContent>
            <TabsContent value="unstake">
              {unstakedRequested ? (
                <div className="border[1px] w-full rounded border border-yellow-500 bg-yellow-500/10 py-2">
                  <h1 className="text-center text-yellow-500">
                    Unstake request is raised
                  </h1>
                </div>
              ) : (
                <div className="mt-2">
                  <Button
                    disabled={isStaking}
                    loading={isStaking}
                    label={`Request to unstake`}
                    onClick={handleUnstakeRequest}
                    widthFull
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
        {/* TABS */}
      </section>
      {/* BODY */}
    </main>
  );
};

export default Stake;
